import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CLICK_GRID_SIZE, MOVE_GRID_COLS, MOVE_GRID_ROWS } from "@heatmap/shared";
import {
  ApiError,
  captureSnapshots,
  fetchHeatmap,
  fetchPage,
  fetchPageStats,
  fetchSnapshotImageUrl,
  fetchSnapshotRects,
  fetchSnapshots,
} from "../lib/api-client";
import { ErrorAlert } from "../components/Alert";
import type {
  ClickHeatmap,
  Heatmap,
  HeatmapDevice,
  HeatmapSegment,
  HeatmapType,
  MoveHeatmap,
  Page,
  PageStats,
  ScrollHeatmap,
  Snapshot,
  SnapshotDevice,
  SnapshotRects,
} from "../lib/types";

// Sequential blue ramp, light->dark (dataviz skill: references/palette.md).
// Used for magnitude encoding (reach %, click intensity, move weight) — never
// for identity, so it stays a single hue across all three heatmap types.
const SEQUENTIAL_BLUE = [
  "#cde2fb", // 100
  "#9ec5f4", // 200
  "#6da7ec", // 300
  "#3987e5", // 400
  "#256abf", // 500
  "#184f95", // 600
  "#0d366b", // 700
];

function blueAt(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const idx = Math.min(SEQUENTIAL_BLUE.length - 1, Math.floor(clamped * SEQUENTIAL_BLUE.length));
  return SEQUENTIAL_BLUE[idx];
}

const SEGMENT_LABELS: Record<HeatmapSegment, string> = {
  all: "すべて",
  new: "新規",
  return: "リピーター",
  cv: "CVあり",
  noncv: "CVなし",
};

const TYPE_LABELS: Record<HeatmapType, string> = {
  scroll: "スクロール",
  click: "クリック",
  move: "マウスムーブ",
};

function formatMs(ms: number): string {
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}秒`;
  const min = Math.floor(sec / 60);
  const rem = Math.round(sec % 60);
  return `${min}分${rem}秒`;
}

function todayJst(): Date {
  // JST = UTC+9; construct a Date whose UTC fields equal the JST wall clock.
  const now = new Date();
  return new Date(now.getTime() + 9 * 60 * 60 * 1000);
}

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgoJst(days: number): string {
  const d = todayJst();
  d.setUTCDate(d.getUTCDate() - days);
  return toDateInput(d);
}

type RangePreset = "7d" | "30d" | "custom";

interface Range {
  from: string;
  to: string;
}

function presetRange(preset: RangePreset, current: Range): Range {
  if (preset === "7d") return { from: daysAgoJst(6), to: daysAgoJst(0) };
  if (preset === "30d") return { from: daysAgoJst(29), to: daysAgoJst(0) };
  return current;
}

export function HeatmapPage() {
  const { pageId } = useParams<{ pageId: string }>();

  const [page, setPage] = useState<Page | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const [type, setType] = useState<HeatmapType>("scroll");
  const [preset, setPreset] = useState<RangePreset>("7d");
  const [range, setRange] = useState<Range>({ from: daysAgoJst(6), to: daysAgoJst(0) });
  const [device, setDevice] = useState<HeatmapDevice>("pc");
  const [segment, setSegment] = useState<HeatmapSegment>("all");

  const [heatmap, setHeatmap] = useState<Heatmap | null>(null);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(true);

  const [stats, setStats] = useState<PageStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [snapshotRects, setSnapshotRects] = useState<SnapshotRects | null>(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(true);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [captureNotice, setCaptureNotice] = useState<string | null>(null);

  const [imgSize, setImgSize] = useState<{ width: number; height: number } | null>(null);
  const [hoverInfo, setHoverInfo] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const snapshotDevice: SnapshotDevice = device === "all" ? "pc" : device;

  // ---- Page ----
  useEffect(() => {
    if (!pageId) return;
    fetchPage(pageId)
      .then(setPage)
      .catch((err) => setPageError(err instanceof ApiError ? err.message : "ページ情報の取得に失敗しました"));
  }, [pageId]);

  // ---- Snapshot (image + rects) ----
  const loadSnapshot = useCallback(() => {
    if (!pageId) return;
    setIsLoadingSnapshot(true);
    setSnapshotError(null);
    fetchSnapshots(pageId, snapshotDevice)
      .then(async (snapshots) => {
        const latest = snapshots[0] ?? null;
        setSnapshot(latest);
        if (!latest) {
          setSnapshotUrl(null);
          setSnapshotRects(null);
          return;
        }
        const [url, rects] = await Promise.all([
          fetchSnapshotImageUrl(latest.id),
          fetchSnapshotRects(latest.id),
        ]);
        setSnapshotUrl(url);
        setSnapshotRects(rects);
      })
      .catch((err) => setSnapshotError(err instanceof ApiError ? err.message : "スクリーンショットの取得に失敗しました"))
      .finally(() => setIsLoadingSnapshot(false));
  }, [pageId, snapshotDevice]);

  useEffect(() => {
    loadSnapshot();
    // Revoke the previous object URL on device change / unmount to avoid leaks.
    return () => {
      setSnapshotUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return prev;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSnapshot]);

  // ---- Heatmap ----
  useEffect(() => {
    if (!pageId) return;
    setIsLoadingHeatmap(true);
    setHeatmapError(null);
    fetchHeatmap(pageId, type, { from: range.from, to: range.to, device, segment })
      .then(setHeatmap)
      .catch((err) => setHeatmapError(err instanceof ApiError ? err.message : "ヒートマップデータの取得に失敗しました"))
      .finally(() => setIsLoadingHeatmap(false));
  }, [pageId, type, range.from, range.to, device, segment]);

  // ---- Stats ----
  useEffect(() => {
    if (!pageId) return;
    setIsLoadingStats(true);
    setStatsError(null);
    fetchPageStats(pageId, { from: range.from, to: range.to, device, segment })
      .then(setStats)
      .catch((err) => setStatsError(err instanceof ApiError ? err.message : "統計データの取得に失敗しました"))
      .finally(() => setIsLoadingStats(false));
  }, [pageId, range.from, range.to, device, segment]);

  function handlePresetChange(next: RangePreset) {
    setPreset(next);
    if (next !== "custom") {
      setRange(presetRange(next, range));
    }
  }

  async function handleCapture() {
    if (!pageId) return;
    setCaptureError(null);
    setCaptureNotice(null);
    setIsCapturing(true);
    try {
      await captureSnapshots(pageId, { devices: [snapshotDevice] });
      setCaptureNotice("撮影をリクエストしました。反映まで少し時間がかかる場合があります。");
      loadSnapshot();
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setCaptureError("Browser Renderingが未有効のため撮影できません");
      } else {
        setCaptureError(err instanceof ApiError ? err.message : "スクリーンショットの取得に失敗しました");
      }
    } finally {
      setIsCapturing(false);
    }
  }

  // Track rendered image width so overlays can scale docWidth -> display width.
  useEffect(() => {
    function measure() {
      const img = containerRef.current?.querySelector("img");
      if (img && img.clientWidth > 0) {
        setImgSize({ width: img.clientWidth, height: img.clientHeight });
      }
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [snapshotUrl]);

  const scale = useMemo(() => {
    if (!imgSize || !snapshotRects) return 1;
    if (snapshotRects.docWidth <= 0) return 1;
    return imgSize.width / snapshotRects.docWidth;
  }, [imgSize, snapshotRects]);

  // ---- Draw click / move overlays on canvas ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgSize) return;
    canvas.width = imgSize.width;
    canvas.height = imgSize.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (type === "click" && heatmap?.type === "click") {
      drawClickHeat(ctx, heatmap, snapshotRects, scale);
    } else if (type === "move" && heatmap?.type === "move") {
      drawMoveHeat(ctx, heatmap, imgSize);
    }
  }, [type, heatmap, imgSize, snapshotRects, scale]);

  if (!pageId) return null;

  const hasSnapshot = !!snapshotUrl && !!imgSize;
  const skippedSelectors =
    type === "click" && heatmap?.type === "click" && snapshotRects
      ? countUnmatchedSelectors(heatmap, snapshotRects)
      : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">ヒートマップ</h1>
          <p className="text-sm text-gray-500 mt-1">{page?.name ?? "..."}</p>
        </div>
        <div className="text-right">
          <button
            type="button"
            onClick={handleCapture}
            disabled={isCapturing}
            className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 whitespace-nowrap"
          >
            {isCapturing ? "撮影中..." : "スクリーンショットを更新"}
          </button>
          {snapshot && (
            <p className="text-xs text-gray-400 mt-1">
              撮影日時: {new Date(snapshot.capturedAt).toLocaleString("ja-JP")}
            </p>
          )}
        </div>
      </div>

      <ErrorAlert message={pageError} />
      <ErrorAlert message={captureError} />
      {captureNotice && (
        <div className="rounded border border-blue-200 bg-blue-50 text-blue-700 text-sm px-3 py-2">
          {captureNotice}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-end gap-4">
        <div className="flex gap-1 border-b border-gray-200">
          {(Object.keys(TYPE_LABELS) as HeatmapType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${
                type === t
                  ? "border-blue-600 text-blue-600 font-medium"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1" htmlFor="hm-range">
            期間
          </label>
          <div className="flex items-center gap-2">
            <select
              id="hm-range"
              value={preset}
              onChange={(e) => handlePresetChange(e.target.value as RangePreset)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="7d">過去7日</option>
              <option value="30d">過去30日</option>
              <option value="custom">カスタム</option>
            </select>
            {preset === "custom" && (
              <>
                <input
                  type="date"
                  value={range.from}
                  onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <span className="text-gray-400 text-sm">〜</span>
                <input
                  type="date"
                  value={range.to}
                  onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1" htmlFor="hm-device">
            デバイス
          </label>
          <select
            id="hm-device"
            value={device}
            onChange={(e) => setDevice(e.target.value as HeatmapDevice)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="pc">PC</option>
            <option value="sp">スマホ</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1" htmlFor="hm-segment">
            セグメント
          </label>
          <select
            id="hm-segment"
            value={segment}
            onChange={(e) => setSegment(e.target.value as HeatmapSegment)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {(Object.keys(SEGMENT_LABELS) as HeatmapSegment[]).map((s) => (
              <option key={s} value={s}>
                {SEGMENT_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stat tiles + goal table */}
      <StatsPanel stats={stats} isLoading={isLoadingStats} error={statsError} />

      {/* Heatmap visualization */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <ErrorAlert message={heatmapError} />
        <ErrorAlert message={snapshotError} />

        {isLoadingSnapshot || isLoadingHeatmap ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : !hasSnapshot ? (
          <div className="space-y-4">
            <div className="rounded border border-amber-200 bg-amber-50 text-amber-700 text-sm px-3 py-2">
              このデバイスのスクリーンショットがまだありません。「スクリーンショットを更新」から撮影するか、下のランキング/数値データのみで確認してください。
            </div>
            {type === "scroll" && heatmap?.type === "scroll" && <ScrollBarFallback heatmap={heatmap} />}
            {type === "click" && heatmap?.type === "click" && <ClickRankingTable heatmap={heatmap} />}
            {type === "move" && <p className="text-sm text-gray-500">マウスムーブはスクリーンショット上にのみ表示できます。</p>}
          </div>
        ) : (
          <>
            <div ref={containerRef} className="relative inline-block max-w-full" onMouseLeave={() => setHoverInfo(null)}>
              {/* eslint-disable-next-line jsx-a11y/alt-text */}
              <img
                src={snapshotUrl ?? undefined}
                alt="ページスクリーンショット"
                className="block max-w-full h-auto rounded border border-gray-200"
                onLoad={(e) => {
                  const el = e.currentTarget;
                  setImgSize({ width: el.clientWidth, height: el.clientHeight });
                }}
              />
              {type === "scroll" && heatmap?.type === "scroll" && imgSize && (
                <ScrollOverlay
                  heatmap={heatmap}
                  height={imgSize.height}
                  width={imgSize.width}
                  onHover={setHoverInfo}
                />
              )}
              {(type === "click" || type === "move") && (
                <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ width: imgSize?.width, height: imgSize?.height }} />
              )}
              {hoverInfo && (
                <div className="absolute top-2 right-2 bg-gray-900 text-white text-xs rounded px-2 py-1.5 pointer-events-none max-w-xs">
                  {hoverInfo}
                </div>
              )}
            </div>

            {type === "click" && heatmap?.type === "click" && (
              <div className="mt-4 space-y-2">
                {skippedSelectors > 0 && (
                  <p className="text-xs text-gray-400">
                    スクリーンショット取得後にDOM構造が変わった等の理由で、{skippedSelectors}件の要素は位置を特定できず表示から除外されています（件数はランキング表に含まれます）。
                  </p>
                )}
                <ClickRankingTable heatmap={heatmap} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatsPanel({
  stats,
  isLoading,
  error,
}: {
  stats: PageStats | null;
  isLoading: boolean;
  error: string | null;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h2 className="text-base font-semibold mb-3">数値データ</h2>
      <ErrorAlert message={error} />
      {isLoading ? (
        <p className="text-sm text-gray-500">読み込み中...</p>
      ) : !stats ? (
        <p className="text-sm text-gray-500">データがありません</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <StatTile label="PV" value={stats.pv.toLocaleString()} />
            <StatTile label="セッション" value={stats.sessions.toLocaleString()} />
            <StatTile label="CVセッション" value={stats.goalSessions.toLocaleString()} />
            <StatTile label="平均滞在時間" value={formatMs(stats.avgDwellMs)} />
          </div>

          {stats.goals.length === 0 ? (
            <p className="text-sm text-gray-500">ゴールが登録されていません</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="px-3 py-2 font-medium">ゴール名</th>
                  <th className="px-3 py-2 font-medium text-right">CV数</th>
                  <th className="px-3 py-2 font-medium text-right">CVR</th>
                </tr>
              </thead>
              <tbody>
                {stats.goals.map((g) => (
                  <tr key={g.goalId} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2">{g.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.conversions.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{(g.cvr * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

// ---- Scroll ----

function ScrollOverlay({
  heatmap,
  width,
  height,
  onHover,
}: {
  heatmap: ScrollHeatmap;
  width: number;
  height: number;
  onHover: (info: string | null) => void;
}) {
  const buckets = fillScrollBuckets(heatmap.buckets);
  const maxReach = Math.max(1, ...buckets.map((b) => b.reach));
  const bucketHeight = height / buckets.length;

  return (
    <div className="absolute inset-0" style={{ width, height }}>
      {buckets.map((b, i) => {
        const ratio = b.reach / maxReach;
        return (
          <div
            key={b.depthPct}
            className="absolute left-0 right-0 cursor-crosshair"
            style={{
              top: i * bucketHeight,
              height: bucketHeight,
              backgroundColor: blueAt(ratio),
              opacity: 0.5,
            }}
            onMouseEnter={() =>
              onHover(
                `到達率${(ratio * 100).toFixed(0)}%（到達${b.reach.toLocaleString()} / 離脱${b.exits.toLocaleString()} / 平均滞在${formatMs(
                  b.dwellMsSum && b.reach ? b.dwellMsSum / b.reach : 0,
                )}）`,
              )
            }
          />
        );
      })}
      {[25, 50, 75, 90].map((mark) => {
        const bucket = buckets.find((b) => b.depthPct >= mark) ?? buckets[buckets.length - 1];
        const pct = bucket.reach / maxReach;
        const top = (mark / 100) * height;
        return (
          <div key={mark} className="absolute left-0 right-0 border-t border-dashed border-white/70" style={{ top }}>
            <span className="absolute left-1 -top-4 text-[10px] font-medium text-white bg-gray-900/70 rounded px-1">
              {mark}%地点 到達{(pct * 100).toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

function fillScrollBuckets(buckets: ScrollHeatmap["buckets"]) {
  const byDepth = new Map(buckets.map((b) => [b.depthPct, b]));
  const filled = [];
  for (let d = 0; d < 100; d += 5) {
    filled.push(byDepth.get(d) ?? { depthPct: d, reach: 0, exits: 0, dwellMsSum: 0 });
  }
  return filled;
}

function ScrollBarFallback({ heatmap }: { heatmap: ScrollHeatmap }) {
  const buckets = fillScrollBuckets(heatmap.buckets);
  const maxReach = Math.max(1, ...buckets.map((b) => b.reach));
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 text-left text-gray-500">
          <th className="px-3 py-2 font-medium">深度</th>
          <th className="px-3 py-2 font-medium">到達率</th>
          <th className="px-3 py-2 font-medium text-right">到達</th>
          <th className="px-3 py-2 font-medium text-right">離脱</th>
        </tr>
      </thead>
      <tbody>
        {buckets.map((b) => {
          const ratio = b.reach / maxReach;
          return (
            <tr key={b.depthPct} className="border-b border-gray-100 last:border-0">
              <td className="px-3 py-2 tabular-nums">{b.depthPct}%</td>
              <td className="px-3 py-2">
                <div className="h-3 rounded" style={{ width: `${Math.max(2, ratio * 100)}%`, backgroundColor: blueAt(ratio) }} />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{b.reach.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums">{b.exits.toLocaleString()}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---- Click ----

// rects carry `selector`, not a hash, so points are matched by selector
// string; selectorHash on ClickPoint is unused here.
function findRect(selector: string, rects: SnapshotRects | null) {
  if (!rects) return null;
  return rects.rects.find((r) => r.selector === selector) ?? null;
}

function countUnmatchedSelectors(heatmap: ClickHeatmap, rects: SnapshotRects): number {
  const known = new Set(rects.rects.map((r) => r.selector));
  const missing = new Set<string>();
  for (const p of heatmap.points) {
    if (!known.has(p.selector)) missing.add(p.selector);
  }
  return missing.size;
}

function drawClickHeat(ctx: CanvasRenderingContext2D, heatmap: ClickHeatmap, rects: SnapshotRects | null, scale: number) {
  if (!rects) return;
  const maxClicks = Math.max(1, ...heatmap.points.map((p) => p.clicks));

  ctx.globalCompositeOperation = "lighter";
  for (const point of heatmap.points) {
    const rect = findRect(point.selector, rects);
    if (!rect) continue;

    const px = (rect.x + (point.gridX / CLICK_GRID_SIZE) * rect.w) * scale;
    const py = (rect.y + (point.gridY / CLICK_GRID_SIZE) * rect.h) * scale;
    const intensity = point.clicks / maxClicks;
    const radius = 18 + intensity * 22;

    const gradient = ctx.createRadialGradient(px, py, 0, px, py, radius);
    gradient.addColorStop(0, hexToRgba(blueAt(0.55 + intensity * 0.45), 0.45 * intensity + 0.15));
    gradient.addColorStop(1, hexToRgba(blueAt(0.55 + intensity * 0.45), 0));

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = "source-over";
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ClickRankingTable({ heatmap }: { heatmap: ClickHeatmap }) {
  const rows = [...heatmap.bySelector].sort((a, b) => b.clicks - a.clicks);
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">クリックデータがありません</p>;
  }
  return (
    <div>
      <h3 className="text-sm font-semibold mb-2">クリック要素ランキング</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-gray-500">
            <th className="px-3 py-2 font-medium">セレクタ</th>
            <th className="px-3 py-2 font-medium text-right">クリック数</th>
            <th className="px-3 py-2 font-medium text-right">CVクリック</th>
            <th className="px-3 py-2 font-medium text-right">クリックCVR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.selectorHash} className="border-b border-gray-100 last:border-0">
              <td className="px-3 py-2 font-mono text-xs">{r.selector}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.clicks.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.cvClicks.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.clicks > 0 ? `${((r.cvClicks / r.clicks) * 100).toFixed(1)}%` : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Move ----

function drawMoveHeat(ctx: CanvasRenderingContext2D, heatmap: MoveHeatmap, imgSize: { width: number; height: number }) {
  // Grid cells are fractions of docWidth/docHeight (rects meta, falling back
  // to the rendered image size when unavailable), so once scaled onto the
  // displayed image the doc dimension cancels out — cell size is just the
  // image size divided by the grid resolution.
  const cellW = imgSize.width / MOVE_GRID_COLS;
  const cellH = imgSize.height / MOVE_GRID_ROWS;
  const maxWeight = Math.max(1, ...heatmap.cells.map((c) => c.weight));

  ctx.globalCompositeOperation = "lighter";
  for (const cell of heatmap.cells) {
    const px = (cell.gridX / MOVE_GRID_COLS) * imgSize.width + cellW / 2;
    const py = (cell.gridY / MOVE_GRID_ROWS) * imgSize.height + cellH / 2;
    const intensity = cell.weight / maxWeight;
    const radius = Math.max(cellW, cellH) * 1.5;

    const gradient = ctx.createRadialGradient(px, py, 0, px, py, radius);
    gradient.addColorStop(0, hexToRgba(blueAt(0.4 + intensity * 0.6), 0.35 * intensity + 0.1));
    gradient.addColorStop(1, hexToRgba(blueAt(0.4 + intensity * 0.6), 0));

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = "source-over";
}
